/**
 * homphyApi.ts -- shared fetch client for the Spring Boot backend
 * -----------------------------------------------------------
 * Drop into src/services/homphyApi.ts. Both EducatorSection and ShopSection
 * import from here so auth/error handling stays in one place.
 *
 * Assumes:
 *  - Base URL from VITE_API_BASE_URL (falls back to /api for same-origin proxy)
 *  - JWT stored in localStorage under 'homphy_token' (adjust if your
 *    AppointmentController/ReviewController auth uses a different key)
 *  - Backend error responses shaped like { message: string, status: number }
 *    -- standard Spring Boot @ControllerAdvice error body. Adjust parseError
 *    if yours differs.
 */

import type { User, AccountType, ProviderRole } from '../types';
import { UserRole } from '../types';

const API_BASE = import.meta.env?.VITE_API_BASE_URL ?? '/api';

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

function getToken(): string | null {
  return localStorage.getItem('homphy_token');
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
      ...options.headers,
    },
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      message = body.message ?? message;
    } catch {
      /* non-JSON error body, keep default message */
    }
    throw new ApiError(message, res.status);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function uploadFile<T>(path: string, formData: FormData): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    body: formData,
    headers: {
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
    },
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      message = body.message ?? message;
    } catch {
      /* non-JSON error body, keep default message */
    }
    throw new ApiError(message, res.status);
  }

  return res.json() as Promise<T>;
}

/** Downloads the Homphy PDF receipt for a confirmed M-Pesa payment (session, course, shop or subscription). */
export async function downloadReceiptForPayment(checkoutRequestId: string): Promise<void> {
  const ref = await request<{ id: number; receiptNumber: string }>(`/receipts/for-payment/${checkoutRequestId}`);
  const token = getToken();
  const res = await fetch(`${API_BASE}/receipts/${ref.id}/pdf`, {
    headers: token ? { Authorization: 'Bearer ' + token } : {},
  });
  if (!res.ok) throw new ApiError('Could not download the receipt (' + res.status + ')', res.status);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = ref.receiptNumber + '.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
/* ---------------------------------------------------------------
   Endpoint contracts below are a best-effort guess at what a Spring
   ShopController need to implement on the backend. Written
   against the same resource-naming convention as
   AppointmentController / ReviewController.
--------------------------------------------------------------- */

/* ---------------------------------------------------------------
 * DROP-IN REPLACEMENT for the old
 * services/homphyApi.ts. Adds course detail fields (description,
 * category, priceKsh, coverImageUrl) and full lesson CRUD/reorder,
 * matching the new backend endpoints under /api/educator/courses/:id
 * and /api/educator/courses/:id/lessons.
 * --------------------------------------------------------------- */

export interface EducatorStats {
  totalStudents: number;
  activeCourses: number;
  totalEarningsKsh: number;
  studentGrowthPct: number; // week over week
}

export interface EducatorCourse {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  priceKsh: number;
  coverImageUrl: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  lessons: number;
  students: number;
  rating: number | null;
  earningsKsh: number;
  updatedAt: string;
}

export interface EducatorLesson {
  id: string;
  title: string;
  position: number;
  contentType: 'VIDEO' | 'TEXT' | 'QUIZ' | 'DOCUMENT';
  contentUrl: string | null;
  contentBody: string | null;
  durationMinutes: number | null;
}

export interface EducatorActivity {
  id: string;
  type: 'ENROLLMENT' | 'REVIEW' | 'PAYOUT' | 'QUESTION' | 'COURSE';
  summary: string;
  createdAt: string;
}

export interface CreateCoursePayload {
  title: string;
}

export interface UpdateCourseDetailsPayload {
  title?: string;
  description?: string;
  category?: string;
  priceKsh?: number;
  coverImageUrl?: string;
}

export interface CreateLessonPayload {
  title: string;
  contentType?: EducatorLesson['contentType'];
  contentUrl?: string;
  contentBody?: string;
  durationMinutes?: number;
}

export interface UpdateLessonPayload {
  title?: string;
  contentType?: EducatorLesson['contentType'];
  contentUrl?: string;
  contentBody?: string;
  durationMinutes?: number;
}

export interface EducatorProfile {
  id: string;
  bio: string | null;
  referralCode?: string | null;
}

export const educatorApi = {
  getProfile: () => api.get<EducatorProfile>('/educator/profile'),
  updateProfile: (payload: { bio: string }) =>
    api.patch<EducatorProfile>('/educator/profile', payload),

  getStats: () => api.get<EducatorStats>('/educator/dashboard/stats'),
  getActivity: () => api.get<EducatorActivity[]>('/educator/dashboard/activity'),

  getCourses: () => api.get<EducatorCourse[]>('/educator/courses'),
  getCourse: (courseId: string) => api.get<EducatorCourse>(`/educator/courses/${courseId}`),
  createCourse: (payload: CreateCoursePayload) =>
    api.post<EducatorCourse>('/educator/courses', payload),
  updateCourseDetails: (courseId: string, payload: UpdateCourseDetailsPayload) =>
    api.patch<EducatorCourse>(`/educator/courses/${courseId}`, payload),
  updateCourseStatus: (courseId: string, status: EducatorCourse['status']) =>
    api.patch<EducatorCourse>(`/educator/courses/${courseId}/status`, { status }),

  getLessons: (courseId: string) =>
    api.get<EducatorLesson[]>(`/educator/courses/${courseId}/lessons`),
  createLesson: (courseId: string, payload: CreateLessonPayload) =>
    api.post<EducatorLesson>(`/educator/courses/${courseId}/lessons`, payload),
  updateLesson: (courseId: string, lessonId: string, payload: UpdateLessonPayload) =>
    api.patch<EducatorLesson>(`/educator/courses/${courseId}/lessons/${lessonId}`, payload),
  deleteLesson: (courseId: string, lessonId: string) =>
    api.del<void>(`/educator/courses/${courseId}/lessons/${lessonId}`),
  reorderLessons: (courseId: string, lessonIdsInOrder: string[]) =>
    api.patch<EducatorLesson[]>(`/educator/courses/${courseId}/lessons/reorder`, { lessonIdsInOrder }),
};
// --- Catalog ---
export interface CatalogCourse {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  priceKsh: number;
  coverImageUrl: string | null;
  lessons: number;
  students: number;
  rating: number | null;
  educatorId: string | null;
}

export interface CatalogPage<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  number: number;
}

export const catalogApi = {
  listCourses: (params?: { q?: string; page?: number; size?: number }) => {
    const search = new URLSearchParams();
    if (params?.q) search.set('q', params.q);
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.size != null) search.set('size', String(params.size));
    const qs = search.toString();
    return api.get<CatalogPage<CatalogCourse>>(`/catalog/courses${qs ? `?${qs}` : ''}`);
  },
  getCourse: (courseId: string) =>
    api.get<CatalogCourse>(`/catalog/courses/${courseId}`),
};

// Ã¢â€â‚¬Ã¢â€â‚¬ Learner (lesson player) Ã¢â€â‚¬Ã¢â€â‚¬
export interface LearnerLesson {
  id: string;
  title: string;
  position: number;
  contentType: 'VIDEO' | 'TEXT' | 'QUIZ' | 'DOCUMENT';
  contentUrl: string | null;
  contentBody: string | null;
  durationMinutes: number | null;
  completed: boolean;
}

export interface LearnerProfile {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string | null;
  bio: string | null;
}

export const learnerApi = {
  getProfile: () => api.get<LearnerProfile>('/learner/profile'),
  updateProfile: (payload: { bio: string }) =>
    api.patch<LearnerProfile>('/learner/profile', payload),

  getLessons: (courseId: string) =>
    api.get<LearnerLesson[]>(`/learner/courses/${courseId}/lessons`),
  completeLesson: (courseId: string, lessonId: string) =>
    api.post<LearnerLesson>(`/learner/courses/${courseId}/lessons/${lessonId}/complete`, {}),
};

// --- Shop ---
export interface ShopProduct {
  description?: string;
  id: string;
  name: string;
  category: string;
  priceKsh: number;
  compareAtKsh: number | null;
  imageUrl: string;
  inStock: boolean;
  rating: number | null;
  reviewCount: number;
}

export type ShopProductSort = 'newest' | 'price_asc' | 'price_desc';
export interface CartLine {
  productId: string;
  quantity: number;
}
export interface CartSummary {
  lines: (CartLine & { product: ShopProduct })[];
  subtotalKsh: number;
}
export interface OrderPayload {
  lines: CartLine[];
  deliveryCounty: string;
  deliveryAddress: string;
  phoneNumber: string; // for M-Pesa / delivery contact
}
export interface Order {
  id: string;
  status: 'PENDING' | 'PAID' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED';
  totalKsh: number;
  createdAt: string;
  deliveryCounty?: string;
  deliveryAddress?: string;
  deliveryFeeKsh?: number;
  receiptNumber?: string | null;
  phoneNumber?: string;
}

export const shopApi = {
  getDeliveryFee: (county?: string) =>
    api.get<number>(`/shop/delivery-fee${county?.trim() ? `?county=${encodeURIComponent(county.trim())}` : ''}`),
  getProducts: (params?: { category?: string; q?: string; sort?: ShopProductSort }) => {
    const cleaned = Object.fromEntries(
      Object.entries(params ?? {}).filter(([, v]) => v !== undefined && v !== '')
    ) as Record<string, string>;
    const qs = new URLSearchParams(cleaned).toString();
    return api.get<ShopProduct[]>(`/shop/products${qs ? `?${qs}` : ''}`);
  },
  getProduct: (productId: string | number) => api.get<ShopProduct>(`/shop/products/${productId}`),
  getCart: () => api.get<CartSummary>('/shop/cart'),
  addToCart: (productId: string, quantity = 1) =>
    api.post<CartSummary>('/shop/cart/items', { productId, quantity }),
  updateCartLine: (productId: string, quantity: number) =>
    api.patch<CartSummary>(`/shop/cart/items/${productId}`, { quantity }),
  removeCartLine: (productId: string) => api.del<CartSummary>(`/shop/cart/items/${productId}`),
  placeOrder: (payload: OrderPayload) => api.post<Order>('/shop/orders', payload),
  listOrders: () => api.get<Order[]>('/shop/orders'),
  cancelOrder: (orderId: string | number) => api.post<void>(`/shop/orders/${orderId}/cancel`),
};
/* ---------------------------------------------------------------
 * APPEND THIS BLOCK TO homphyApi.ts (below the existing shopApi block)
 * Matches backend contract exactly:
 *   POST /api/auth/register  -> RegisterRequest  -> AuthResponse
 *   POST /api/auth/login     -> LoginRequest     -> AuthResponse
 * --------------------------------------------------------------- */

// --- Auth ---
export interface AuthResponse {
  token: string;
  email: string;
  role: string;        // primary/most-privileged: ADMIN | CLIENT | <ProviderRole>
  roles: string[];      // full provider cadre list (empty for CLIENT/ADMIN)
  isLearner: boolean;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface RegisterPayload {
  fullName: string;
  email: string;
  password: string;
  phoneNumber?: string;
  roles?: string[];     // ProviderRole names, e.g. ['PHYSIOTHERAPY']. Omit/empty => CLIENT account.
  isLearner?: boolean;  // optional -- defaults to false on the backend
}

const TOKEN_KEY = 'homphy_token';

export const authApi = {
  login: (payload: LoginPayload) =>
    api.post<AuthResponse>('/auth/login', payload),

  register: (payload: RegisterPayload) =>
    api.post<AuthResponse>('/auth/register', payload),

  saveToken: (token: string) => localStorage.setItem(TOKEN_KEY, token),

  clearToken: () => localStorage.removeItem(TOKEN_KEY),

  getToken: () => localStorage.getItem(TOKEN_KEY),

  isAuthenticated: () => !!localStorage.getItem(TOKEN_KEY),
};

// -- User / Auth --
// Mirrors backend UserDTO (com.homphy.homphybackend.dto.UserDTO) exactly.
// Field names match the Java record's JSON serialization (camelCase).
export interface UserDTO {
  id: string;
  uid: string;
  name: string;
  email: string;
  phone: string;
  accountType: 'ADMIN' | 'CLIENT' | 'PROVIDER';
  role: string;        // 'ADMIN' | 'CLIENT' | a ProviderRole cadre value
  roles: string[];      // full cadre list, empty for CLIENT/ADMIN
  isActive: boolean;
  verified: boolean;
  avatar: string | null;
  kycStatus: string;
  publicKey: string | null;
  consentFlags: { location: boolean; dataProcessing: boolean; aiRecommendations: boolean; marketing: boolean } | null;
  acceptedTermsVersion: string | null;
  nationalIdNumber: string | null;
  nationalIdUrl: string | null;
  lastLocation: { latitude: number; longitude: number } | null;
  deleted: boolean;
  deactivationReason: string | null;
  deactivatedAt: string | null;
  deactivationType: string | null;
  accountSuspendedByUser: boolean;
  isLearner: boolean;
  workforceIntakeCompleted: boolean;
  workforceIntakeDeadline: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Maps the backend UserDTO onto the frontend User shape.
 *
 * Two deliberate lossy/special-case mappings, both interim until the backend
 * grows real support:
 *  - DOCTOR (old frontend-only concept) <-> backend ProviderRole.REFERRAL_GP.
 *    Not a confirmed clinical-equivalence decision, just the closest existing
 *    cadre -- revisit if Doctor and GP-referral ever need to diverge.
 *  - LEARNER (old frontend-only concept) <-> backend accountType CLIENT with
 *    isLearner = true. Backend has no LEARNER account type or cadre.
 *
 * `role` (deprecated UserRole) is derived for backward compat with the ~13
 * files not yet converted to accountType/primaryRole/roles. Remove once
 * those are all migrated.
 */
export function dtoToUser(dto: UserDTO): User {
  // roles[] passes through as-is -- REFERRAL_GP (mapped from DOCTOR) and every
  // other cadre value are valid ProviderRole members, no filtering needed.
  const roles: ProviderRole[] = dto.roles.map((r) => r as ProviderRole);

  let legacyRole: UserRole;
  if (dto.accountType === 'ADMIN') {
    legacyRole = UserRole.ADMIN;
  } else if (dto.accountType === 'CLIENT') {
    legacyRole = dto.isLearner ? UserRole.LEARNER : UserRole.CLIENT;
  } else {
    // PROVIDER
    switch (dto.role) {
      case 'PHYSIOTHERAPY': legacyRole = UserRole.PHYSIO; break;
      case 'REFERRAL_GP': legacyRole = UserRole.DOCTOR; break;
      case 'CLINIC': legacyRole = UserRole.CLINIC; break;
      case 'EDUCATOR': legacyRole = UserRole.EDUCATOR; break;
      // Cadres with no legacy equivalent (OCCUPATIONAL_THERAPY,
      // SPEECH_LANGUAGE_THERAPY, ORTHOTICS_PROSTHETICS, REHAB_PSYCHOLOGY,
      // PHYSIATRY, REHAB_NURSING, REFERRAL_CHW) fall back to PHYSIO so old
      // call sites still route somewhere sane rather than hitting `undefined`.
      default: legacyRole = UserRole.PHYSIO; break;
    }
  }

  return {
    id: dto.id,
    uid: dto.uid,
    role: legacyRole,
    accountType: dto.accountType as AccountType,
    primaryRole: (dto.accountType === 'PROVIDER' ? dto.role : dto.accountType) as AccountType | ProviderRole,
    roles,
    isLearner: dto.isLearner,
    name: dto.name,
    phone: dto.phone,
    email: dto.email,
    avatar: dto.avatar ?? undefined,
    isActive: dto.isActive,
    kycStatus: (dto.kycStatus as 'PENDING' | 'VERIFIED' | 'REJECTED') ?? undefined,
    publicKey: dto.publicKey ?? undefined,
    consentFlags: dto.consentFlags ?? undefined,
    acceptedTermsVersion: dto.acceptedTermsVersion ?? undefined,
    nationalIdNumber: dto.nationalIdNumber ?? undefined,
    nationalIdUrl: dto.nationalIdUrl ?? undefined,
    lastLocation: dto.lastLocation ?? undefined,
    deleted: dto.deleted,
    createdAt: dto.createdAt,
    deactivationReason: dto.deactivationReason ?? undefined,
    deactivatedAt: dto.deactivatedAt ?? undefined,
    deactivationType: (dto.deactivationType as 'admin' | 'auto_onboarding' | 'auto_dormancy' | 'user_suspended') ?? undefined,
    accountSuspendedByUser: dto.accountSuspendedByUser,
    workforceIntakeCompleted: dto.workforceIntakeCompleted,
    workforceIntakeDeadline: dto.workforceIntakeDeadline ?? undefined,
  };
}

export const userApi = {
  getMe: () => api.get<UserDTO>('/users/me').then(dtoToUser),
};


// --- Admin: Shop Product Management ---
export interface AdminShopProduct {
  id: string;
  name: string;
  description: string | null;
  category: string;
  priceKsh: number;
  compareAtKsh: number | null;
  inStock: boolean;
  imageUrl: string | null;
  rating: number | null;
  reviewCount: number;
}

export interface AdminShopProductPayload {
  name: string;
  description?: string;
  category: string;
  priceKsh: number;
  compareAtKsh?: number;
  inStock: boolean;
  stockQty: number;
  imageUrl?: string;
}

export const adminShopApi = {
  getAllProducts: () => api.get<AdminShopProduct[]>('/shop/admin/products'),
  createProduct: (payload: AdminShopProductPayload) =>
    api.post<AdminShopProduct>('/shop/admin/products', payload),
  updateProduct: (id: string, payload: AdminShopProductPayload) =>
    api.put<AdminShopProduct>(`/shop/admin/products/${id}`, payload),
  deleteProduct: (id: string) => api.del<void>(`/shop/admin/products/${id}`),
};
/* ---------------------------------------------------------------
 * APPEND THIS BLOCK TO homphyApi.ts (below adminShopApi)
 * Matches backend contract exactly:
 *   GET  /api/referrals/inbox/{professionalId}
 *   GET  /api/referrals/sent/{professionalId}
 *   GET  /api/referrals/pooled/{professionTypeId}
 *   POST /api/referrals
 *   POST /api/referrals/{id}/claim?professionalId=X
 *   POST /api/referrals/{id}/view
 *   POST /api/referrals/{id}/accept
 *   POST /api/referrals/{id}/reject
 *   POST /api/referrals/{id}/complete
 * --------------------------------------------------------------- */

// --- Referrals ---
export type ReferralMode = 'DIRECT' | 'POOLED';
export type ReferralType = 'INTERNAL' | 'EXTERNAL';
export type ReferralStatus = 'PENDING' | 'VIEWED' | 'ACCEPTED' | 'COMPLETED' | 'REJECTED' | 'EXPIRED';

export interface ReferralProfessionalSummary {
  id: string;
}

export interface Referral {
  id: string;
  patient: { id: string };
  referringProfessional: ReferralProfessionalSummary;
  referredTo: ReferralProfessionalSummary | null;
  targetProfession: { id: string; displayName?: string } | null;
  referralType: ReferralType;
  referralMode: ReferralMode;
  referralStatus: ReferralStatus;
  clinicalNotes: string | null;
  rejectionReason: string | null;
  viewedAt: string | null;
  respondedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReferralPayload {
  patientId: string;
  referringProfessionalId: string;
  referredToId?: string;
  targetProfessionId?: string;
  referralMode: ReferralMode;
  referralType: ReferralType;
  clinicalNotes: string;
}

export const referralApi = {
  getInbox: (professionalId: string) =>
    api.get<Referral[]>(`/referrals/inbox/${professionalId}`),

  getSent: (professionalId: string) =>
    api.get<Referral[]>(`/referrals/sent/${professionalId}`),

  getPooled: (professionTypeId: string) =>
    api.get<Referral[]>(`/referrals/pooled/${professionTypeId}`),

  create: (payload: CreateReferralPayload) =>
    api.post<Referral>('/referrals', payload),

  claim: (referralId: string, professionalId: string) =>
    api.post<Referral>(`/referrals/${referralId}/claim?professionalId=${professionalId}`),

  markViewed: (referralId: string) =>
    api.post<Referral>(`/referrals/${referralId}/view`),

  accept: (referralId: string) =>
    api.post<Referral>(`/referrals/${referralId}/accept`),

  reject: (referralId: string, reason: string) =>
    api.post<Referral>(`/referrals/${referralId}/reject`, { reason }),

  complete: (referralId: string) =>
    api.post<Referral>(`/referrals/${referralId}/complete`),
};


/* ---------------------------------------------------------------
 * APPEND THIS BLOCK TO homphyApi.ts (below referralApi)
 * Matches backend contract exactly:
 *   GET /api/profiles/search?professionTypeId={id}&county={optional}
 * --------------------------------------------------------------- */

// --- Professional Search (cross-cadre provider picker for DIRECT referrals) ---
export interface ProfessionalSearchResult {
  id: string;
  fullName: string;
  county: string | null;
  professionTypeId: string;
  professionDisplayName: string;
}

export const professionalSearchApi = {
  search: (professionTypeId: string, county?: string) => {
    const params = new URLSearchParams({ professionTypeId });
    if (county) params.set('county', county);
    return api.get<ProfessionalSearchResult[]>(`/profiles/search?${params.toString()}`);
  },
};

/* ---------------------------------------------------------------
 * Workforce Planning Dashboard
 * --------------------------------------------------------------- *
 * NOTE: these types were originally written against a planned
 * contract (totalProviders / underutilizedCount / totalAvailable-
 * HoursPerWeek / professionTypeId / dormantPct) that the deployed
 * backend does NOT actually implement. Updated below to match the
 * real response from GET /api/workforce/summary, confirmed via
 * DevTools against https://homphy.onrender.com/api/workforce/summary:
 *
 *   { total, unemployedPct, underutilized, incomeNotEnoughPct,
 *     totalHoursAvailable, employmentBreakdown, cadres: [
 *       { professionCode, professionDisplayName, verified, total, byCounty }
 *     ] }
 *
 * `dormantPct` is NOT returned by the backend yet -- kept optional
 * here so callers don't assume it's always present. Also note the
 * backend currently counts ALL registered providers matching the
 * filters, not just active ones -- there is no status/active/
 * verified filter param support yet even though UserDTO already
 * has isActive/verified/deleted fields. See backend TODO below.
 * --------------------------------------------------------------- */

// --- Workforce Planning Dashboard ---
export interface WorkforceCountyBreakdown {
  county: string; // '' = provider has no county on file
  count: number;
}

export interface WorkforceCadreSummary {
  professionCode: string;
  professionDisplayName: string;
  verified: boolean;
  total: number;
  byCounty: WorkforceCountyBreakdown[];
}

export interface WorkforceEmploymentBreakdown {
  status: 'FULL_TIME_ELSEWHERE' | 'PART_TIME_ELSEWHERE' | 'UNEMPLOYED_SEEKING' | 'SELF_EMPLOYED' | 'RECENT_GRADUATE';
  count: number;
}

export interface WorkforceJoinReasonBreakdown {
  reason: string;
  count: number;
}

export interface WorkforcePracticeSettingBreakdown {
  setting: string;
  count: number;
}

export interface WorkforceSummary {
  total: number;
  verifiedCount: number;
  unemployedPct: number;
  underutilized: number;
  incomeNotEnoughPct: number;
  totalHoursAvailable: number;
  /** % of providers with no service_access_event activity in the trailing 90 days. */
  dormantPct: number;
  employmentBreakdown: WorkforceEmploymentBreakdown[];
  cadres: WorkforceCadreSummary[];
  /** Architecture doc §2.2. Backend now returns this. */
  joinReasonBreakdown?: WorkforceJoinReasonBreakdown[];
  /** Architecture doc §2.2. Backend now returns this (practiceSetting already existed on User). */
  practiceSettingBreakdown?: WorkforcePracticeSettingBreakdown[];
}

export interface WorkforceCadreOption {
  id: string;
  name: string;
}

export const workforceApi = {
  // TODO(backend): add an `activeOnly` (or `status`) param once the
  // summary query filters by UserDTO.isActive/verified/deleted --
  // right now this endpoint has no way to ask for active-only counts,
  // it always returns every registered provider matching profession/county.
  getSummary: (params?: { professionTypeId?: string; county?: string; activeOnly?: boolean }) => {
    const search = new URLSearchParams();
    if (params?.professionTypeId) search.set('professionTypeId', params.professionTypeId);
    if (params?.county) search.set('county', params.county);
    if (params?.activeOnly) search.set('activeOnly', 'true');
    const qs = search.toString();
    return api.get<WorkforceSummary>(`/workforce/summary${qs ? `?${qs}` : ''}`);
  },

  getCadres: () => api.get<WorkforceCadreOption[]>('/workforce/cadres'),

  getCounties: () => api.get<string[]>('/workforce/counties'),
};

export interface CountyActivityDTO {
  county: string;
  professionTypeId: number | null;
  period: string;
  patientsServed: number;
  totalServiceRequests: number;
  activeProviderCount: number;
  verifiedProviderCount: number;
}

export interface DashboardVisibilityDTO {
  public: boolean;
}

export type PublicActivityResponse = { visible: false } | CountyActivityDTO[];

export const accessImpactApi = {
  getInternalActivity: () =>
    api.get<CountyActivityDTO[]>('/dashboards/access-impact/internal/activity'),

  getInternalUnderserved: () =>
    api.get<CountyActivityDTO[]>('/dashboards/access-impact/internal/underserved'),

  getVisibility: () =>
    api.get<DashboardVisibilityDTO>('/dashboards/access-impact/visibility'),

  setVisibility: (isPublic: boolean) =>
    api.post<DashboardVisibilityDTO>('/dashboards/access-impact/visibility', { public: isPublic }),

  getPublicActivity: () =>
    api.get<PublicActivityResponse>('/dashboards/access-impact/public/activity'),
};


// --- Certificates (public verification) ---
export interface CertificateVerification {
  code: string;
  status: 'VALID' | 'REVOKED';
  holderName: string;
  courseTitle: string;
  instructorName: string | null;
  durationMinutes: number | null;
  lessonCount: number | null;
  completedAt: string | null;
  issuedAt: string | null;
  pdfUrl: string | null;
}

export const certificateApi = {
  verify: (code: string) =>
    api.get<CertificateVerification>(`/certificates/verify/${encodeURIComponent(code)}`),
};
